import React, { useContext, useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import ModalPortal from '../common/ModalPortal';
import ModalStackContext from '../../context/ModalStackContext';
import '../../styles/modal.css';

/**
 * R5 DESKTOP STANDARD, RULE 5 — THE FOUR VARIANTS (Fable + operator,
 * 2026-10-25). ONE dialog component; a `variant` picks its width and its
 * shape:
 *
 *   form    — 560px, a form's fields
 *   confirm — 420px, one question and its answer
 *   picker  — 640px, `search` sits on top (it does not scroll), the list
 *             in the body scrolls under it (`search` is drawn the same way
 *             on any dialog that passes it, variant or not)
 *   wide    — 900px, a table or a two-column record
 *
 * Every variant: a sticky header (title + the ghost ×, labelled "Close"),
 * a body that scrolls, and a sticky footer that holds — in this order —
 * the REFUSAL (a red sentence, `role="alert"`, above the buttons: never a
 * toast, never inline-only) and the buttons, right-aligned, Cancel
 * (secondary) LEFT of the ONE primary. Pass `primaryAction` and the
 * component draws both buttons itself; pass `footer` to draw your own row
 * (the refusal still sits above it). 44px targets on a coarse pointer come
 * from the ff-btn family (buttons.css) and the variant rules (modal.css).
 *
 * NO `variant` = today's behaviour, byte for byte: the 880px default, the
 * caller's `maxWidth`, the `footer` row exactly as it was. No existing
 * caller changes until its batch migrates it. *
 * R-UI-LEFTOVERS (2026-11-03) — two additions for the drawn buttons:
 *   - `primaryTone="danger"`: the ONE act is destructive, so the drawn
 *     primary is the SOLID red (`ff-btn--danger-solid`, Design System v1
 *     §2.2: solid red only on a dialog's confirm step) instead of Froggy
 *     blue. Default "primary". The button keeps `data-modal-primary`.
 *   - HOOKS: any `data-*` key on `primaryAction` (`'data-save-type': '1'`,
 *     `'data-testid': …`) is put on the drawn primary, and `cancelAttrs`'
 *     `data-*` keys on the drawn Cancel — so a caller moving onto the
 *     drawn buttons keeps the hooks its tests and the Cowork read. Only
 *     `data-*` passes; nothing else on the button can be overridden.
 *   - `pickerLayer`: the photo pickers' tier (--ff-z-modal-picker, 1200/
 *     1205), above every ff-modal tier and below `overPicker` — what the
 *     image picker had as a hand-built sheet on the ModalStack's layer.
 */
export const MODAL_VARIANTS = {
  form: 'var(--ff-modal-width-form)',
  confirm: 'var(--ff-modal-width-confirm)',
  picker: 'var(--ff-modal-width-picker)',
  wide: 'var(--ff-modal-width-wide)',
};

const LEGACY_MAX_WIDTH = '880px';

/**
 * R5 B4 review (NIT c) — what the Tab trap cycles through. EVERY input
 * type but hidden (the old list named text/radio/checkbox/number/
 * datetime-local only, so a date or time field was skipped by the trap's
 * first/last reckoning).
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
].join(', ');

/** The `data-*` keys of an object (the hooks a caller may put on a drawn
 *  button), and nothing else. */
const dataHooks = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([k]) => k.startsWith('data-')),
);

function VariantFooter({ refusal, footer, primaryAction, primaryTone, cancelLabel, cancelAttrs, onCancel, saving }) {
  const hasButtons = Boolean(footer || primaryAction);
  if (!refusal && !hasButtons) return null;
  return (
    <div className="ff-modal-footer ff-modal-footer--standard">
      {refusal ? (
        <p className="ff-modal-refusal" role="alert" data-modal-refusal="1">{refusal}</p>
      ) : null}
      {hasButtons ? (
        <div className="ff-modal-actions">
          {footer || (
            <>
              <button
                type="button"
                className="ff-btn ff-btn--secondary"
                onClick={onCancel}
                disabled={saving}
                {...dataHooks(cancelAttrs)}
                data-modal-cancel="1"
              >
                {cancelLabel}
              </button>
              <button
                type={primaryAction.type || 'button'}
                form={primaryAction.form}
                className={`ff-btn ${primaryTone === 'danger' ? 'ff-btn--danger-solid' : 'ff-btn--primary'}`}
                onClick={primaryAction.onClick}
                disabled={Boolean(primaryAction.disabled)}
                aria-busy={primaryAction.busy ? 'true' : undefined}
                {...dataHooks(primaryAction)}
                data-modal-primary="1"
              >
                {primaryAction.busy && primaryAction.busyLabel
                  ? primaryAction.busyLabel : primaryAction.label}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * R5 B2 review (FIX 5) — ONE body scroll lock, reference-counted across
 * every open ui/Modal. The first acquire records the body's own overflow
 * and locks; only the release that brings the count back to zero restores
 * it and removes `ff-modal-open`.
 */
let scrollLockCount = 0;
let scrollLockOriginalOverflow = '';

function acquireScrollLock() {
  if (scrollLockCount === 0) {
    scrollLockOriginalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ff-modal-open');
  }
  scrollLockCount += 1;
}

function releaseScrollLock() {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = scrollLockOriginalOverflow || '';
    document.body.classList.remove('ff-modal-open');
  }
}

/** How many ui/Modal scroll locks are held (for tests). */
export function scrollLockDepth() {
  return scrollLockCount;
}

const inInertSubtree = (el) => {
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
    if (n.inert === true || n.hasAttribute('inert')) return true;
  }
  return false;
};

/**
 * R5 B2 review (FIX 6) — give the focus back to the dialog's opener: at
 * once when it can take it (an opener inside another dialog is not
 * inert), otherwise on the first frame after its inert ancestor is lifted
 * (ModalStackContext lifts it when the stack empties — a frame or two).
 * It never takes the focus from something that got it in the meantime
 * (a dialog opened straight after this one closed).
 */
const FOCUS_RETRY_FRAMES = 10;
function restoreFocus(el) {
  if (!el || typeof el.focus !== 'function') return;
  if (!inInertSubtree(el)) {
    el.focus();
    return;
  }
  const focusLost = () => !document.activeElement || document.activeElement === document.body;
  const attempt = (framesLeft) => {
    if (!el.isConnected || !focusLost()) return;
    if (!inInertSubtree(el)) {
      el.focus();
      return;
    }
    if (framesLeft <= 0) return;
    const next = () => attempt(framesLeft - 1);
    if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(next);
    else setTimeout(next, 16);
  };
  // A microtask first: React commits the stack pop (and the inert lift)
  // synchronously after a click, so usually this is the only wait.
  Promise.resolve().then(() => attempt(FOCUS_RETRY_FRAMES));
}

export function Modal({ isOpen, onClose, title, children, footer, maxWidth: maxWidthProp, compact = false, overDrawer = false, overModal = false, overPicker = false, pickerLayer = false, modalId, variant, refusal, primaryAction, primaryTone = 'primary', cancelLabel = 'Cancel', cancelAttrs, onCancel, search, suspendBeneath = false, sheetClassName, sheetAttrs }) {
  const standard = Object.prototype.hasOwnProperty.call(MODAL_VARIANTS, variant || '');
  // An explicit `maxWidth` always wins; a variant names its own; neither
  // is the 880px this component has always had.
  const maxWidth = maxWidthProp || (standard ? MODAL_VARIANTS[variant] : LEGACY_MAX_WIDTH);
  const modalRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  // Track if modal was just opened to avoid refocusing on every render
  const hasInitialFocusRef = useRef(false);
  // Store onClose in ref to avoid effect re-runs when callback reference changes
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // R5 B4 review (FIX 1) — A SAVE IN FLIGHT IS NEVER ORPHANED. While the
  // primary act is busy (`primaryAction.busy`), every way out is shut: the
  // drawn Cancel and the header × are disabled, and Escape and a backdrop
  // click do nothing. The old editor footers had `disabled={saving}` on
  // Cancel; this is that rule, once, for every caller. In a ref so the
  // keydown effect reads the current value without re-subscribing.
  const saving = Boolean(primaryAction && primaryAction.busy);
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const closeUnlessSaving = () => {
    if (savingRef.current) return;
    onCloseRef.current();
  };

  // S3-03.2 — SUSPENSION (see ModalStackContext). When a dialog that is
  // not a ui/Modal is layered on top of this one, this modal stops being
  // the top dialog and must behave like it: Escape belongs to the thing
  // on top, and the Tab trap must let go so focus can reach that form.
  // Read through the raw context, not `useModalStack()`, which THROWS
  // outside a provider — this primitive is rendered in tests and stories
  // that have no ModalStackProvider, and a keyboard nicety must not be
  // the thing that takes them down.
  //
  // In a REF so the keydown effect does not re-subscribe on every
  // suspend/resume; the handler reads the current value when a key is
  // actually pressed.
  const modalStack = useContext(ModalStackContext);
  const suspendedRef = useRef(false);
  // R5 B2 — `suspendBeneath`: THIS dialog is the one layered on top (the
  // event Log form and the routine-run form, opened over the edit-animal
  // modal or a drawer), so while it is open it claims the suspension of
  // every ui/Modal beneath it — the claim the two forms used to make by
  // hand before they moved onto this component. Its OWN claim never
  // suspends itself: only a claim by some other dialog does.
  const ownSuspendId = useId();
  // R5 B2 review (FIX 1): the heading's id is THIS dialog's own. A fixed
  // `ff-modal-title` was shared by every ui/Modal, so a stacked dialog's
  // aria-labelledby resolved to the heading of the dialog underneath.
  const titleId = `ff-modal-title-${ownSuspendId.replace(/[^A-Za-z0-9_-]/g, '')}`;
  // R-UI-LEFTOVERS A2 — ONLY A CLAIM MADE AFTER MINE SUSPENDS ME. Claims
  // are made on open, so their order is the stacking order: a claimant is
  // suspended by the claims made after its own (the dialogs on top of it),
  // never by one made before it. Two claimants stacked — the image picker
  // (over the dialog it was opened from) and the remove-photo confirm
  // asked from it — used to suspend EACH OTHER, and Escape then reached
  // neither. A dialog that makes no claim is suspended by any claim, as
  // before.
  const claims = modalStack && Array.isArray(modalStack.suspendedBy)
    ? modalStack.suspendedBy : null;
  const ownClaimAt = claims ? claims.indexOf(ownSuspendId) : -1;
  const others = claims
    ? (ownClaimAt >= 0 ? claims.slice(ownClaimAt + 1) : claims)
    : null;
  suspendedRef.current = others
    ? others.length > 0
    : !!(modalStack && modalStack.isSuspended);
  const suspendModals = modalStack && modalStack.suspendModals;
  const resumeModals = modalStack && modalStack.resumeModals;
  useEffect(() => {
    if (!isOpen || !suspendBeneath || !suspendModals || !resumeModals) return undefined;
    suspendModals(ownSuspendId);
    return () => resumeModals(ownSuspendId);
  }, [isOpen, suspendBeneath, suspendModals, resumeModals, ownSuspendId]);

  // Lock body scroll when modal is open with robust cleanup
  useEffect(() => {
    if (isOpen) {
      // Store previously focused element
      previousActiveElementRef.current = document.activeElement;

      // R5 B2 review (FIX 5): the scroll lock is REFERENCE-COUNTED. With
      // two dialogs stacked, closing the TOP one used to strip
      // `body.ff-modal-open` (and the overflow) while the base was still
      // open; only the LAST open ui/Modal releases it now. The cleanup
      // below also runs on unmount, so a route change still releases.
      acquireScrollLock();

      // Focus modal after it's rendered - only on initial open
      if (!hasInitialFocusRef.current) {
        hasInitialFocusRef.current = true;
        setTimeout(() => {
          if (modalRef.current) {
            modalRef.current.focus();
          }
        }, 0);
      }

      // Handle escape key and focus trap
      const handleKeyDown = (e) => {
        // Something is layered on top: both keys belong to it.
        if (suspendedRef.current) return;

        if (e.key === 'Escape') {
          if (savingRef.current) return;
          onCloseRef.current();
          return;
        }

        // Focus trap
        if (e.key === 'Tab' && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            FOCUSABLE_SELECTOR
          );
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        releaseScrollLock();
        document.removeEventListener('keydown', handleKeyDown);

        // Restore focus to the opener — AFTER the page stops being inert
        // (R5 B2 review, FIX 6). ModalStackContext lifts `inert` from
        // `.app-content` only once this dialog has left the stack, which
        // is after this cleanup runs, and a browser silently refuses to
        // focus an element inside an inert subtree.
        restoreFocus(previousActiveElementRef.current);
      };
    } else {
      // Reset the initial focus flag when modal closes
      hasInitialFocusRef.current = false;
    }
  }, [isOpen]); // Removed onClose from deps - use ref instead

  if (!isOpen) return null;
  
  // Render modal using ModalPortal
  return (
    <ModalPortal isOpen={isOpen} modalId={modalId}>
      {/* z-index lives in CSS, never inline: .ff-modal-overlay /
          .ff-modal-sheet pin theirs with !important, which beats any
          inline style per the cascade. `overDrawer` opts into the
          --over-drawer modifiers (1110/1115), the only way to clear
          .shipment-drawer (1100). A prop that set style.zIndex was
          silently inert for as long as it existed. */}
      <div
        className={`ff-modal-overlay ff-stable is-active${overPicker ? ' ff-modal-overlay--over-picker' : pickerLayer ? ' ff-modal-overlay--picker-layer' : overModal ? ' ff-modal-overlay--over-modal' : overDrawer ? ' ff-modal-overlay--over-drawer' : ''}`}
        onClick={closeUnlessSaving}
      >
        <div 
          ref={modalRef}
          {...(sheetAttrs || {})}
          className={`ff-modal-sheet ff-stable ${compact ? 'ff-modal-sheet--sm' : ''}${overPicker ? ' ff-modal-sheet--over-picker' : pickerLayer ? ' ff-modal-sheet--picker-layer' : overModal ? ' ff-modal-sheet--over-modal' : overDrawer ? ' ff-modal-sheet--over-drawer' : ''}${standard ? ` ff-modal-sheet--standard ff-modal-sheet--${variant}` : ''}${sheetClassName ? ` ${sheetClassName}` : ''}`}
          data-variant={standard ? variant : undefined}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth,
            width: `min(${maxWidth}, calc(100vw - calc(var(--ff-space-2xl) * 2)))`,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
        >
          <div className="ff-modal-header">
            <h2 id={titleId} className="ff-modal-title">{title}</h2>
            {/* Design System v1 §2.3 (Fable ruling, Phase 3): the close is
                the family's 40×40 GHOST ICON square, app-wide — never the
                old 32px / 6px-radius one-off. `ff-modal-close` stays as a
                HOOK (the header's spacing, tests); every visual rule is the
                family's. Same label, same handler: Escape and the focus
                return are the sheet's own effects above, untouched. */}
            <button
              type="button"
              className="ff-modal-close ff-btn ff-btn--ghost ff-btn--icon"
              aria-label="Close"
              onClick={closeUnlessSaving}
              disabled={saving}
            >
              <span aria-hidden="true" className="ff-modal-close__glyph">×</span>
            </button>
          </div>
          {/* The picker's search sits between the header and the list,
              and does NOT scroll with it. Honoured with or without a
              `variant` (R5 B1 review): a `search` passed to a legacy
              dialog is drawn, never silently dropped. */}
          {search ? (
            <div className="ff-modal-search" data-modal-search="1">{search}</div>
          ) : null}
          {/* NO EMPTY BODY (Cowork QA on beta, 2026-10-27): a confirm whose
              only words are its refusal (Void batch… on a media mix
              cultures drew from) drew an empty padded strip above the red
              sentence. A body with nothing to show is not drawn. */}
          {React.Children.toArray(children).length > 0 ? (
            <div className="ff-modal-body">
              {children}
            </div>
          ) : null}
          {standard || refusal || primaryAction ? (
            <VariantFooter
              refusal={refusal}
              footer={footer}
              primaryAction={primaryAction}
              primaryTone={primaryTone}
              cancelLabel={cancelLabel}
              cancelAttrs={cancelAttrs}
              onCancel={onCancel || onClose}
              saving={saving}
            />
          ) : footer && (
            <div className="ff-modal-footer">
              {footer}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

Modal.propTypes = {
  overDrawer: PropTypes.bool,
  overModal: PropTypes.bool,
  // Asked from the image picker, which sits above every ff-modal tier.
  overPicker: PropTypes.bool,
  // R-UI-LEFTOVERS A2: THE photo pickers' own tier (see the header).
  pickerLayer: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // A node since R5 B2: the Log form's title carries its target in a
  // muted span ("Log Shed · Mango").
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  maxWidth: PropTypes.string,
  compact: PropTypes.bool,
  // R5 rule 5 — see MODAL_VARIANTS above.
  variant: PropTypes.oneOf(Object.keys(MODAL_VARIANTS)),
  // A refusal SENTENCE (the server's own words), shown in red above the
  // buttons with role="alert".
  refusal: PropTypes.node,
  // The ONE primary act; the component draws Cancel to its left.
  primaryAction: PropTypes.shape({
    label: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit']),
    form: PropTypes.string,
    busy: PropTypes.bool,
    busyLabel: PropTypes.node,
  }),
  // R-UI-LEFTOVERS: "danger" draws the primary as the solid red (a
  // destructive confirm); see the header.
  primaryTone: PropTypes.oneOf(['primary', 'danger']),
  cancelLabel: PropTypes.node,
  // R-UI-LEFTOVERS: `data-*` hooks for the drawn Cancel (others ignored).
  cancelAttrs: PropTypes.object,
  // Cancel's handler; defaults to onClose.
  onCancel: PropTypes.func,
  // The search row on top (does not scroll) — the picker's, but drawn on
  // any dialog that passes it.
  search: PropTypes.node,
  // R5 B2 — claim the suspension of every ui/Modal beneath this one
  // while it is open (see the effect above).
  suspendBeneath: PropTypes.bool,
  // R5 B2 — a class and data-* hooks on the SHEET itself, for a caller
  // that styles or is found by its own sheet (`ev-sheet`,
  // `data-log-event-modal`).
  sheetClassName: PropTypes.string,
  sheetAttrs: PropTypes.object,
};

export default Modal;